import React, { useCallback, useEffect, useState } from "react";
import AddCaddieModal from "../components/ems/AddCaddieModal";
import { attachAddCaddieOption, toCaddieOption } from "../utils/addCaddieOption";
import { snapshotFormioInstanceData } from "../utils/snapshotFormioInstanceData";
import {
  buildCaddiesById,
  clearCaddieOptionsCache,
  getCaddieDatagridLayout,
  injectCaddieSelectValues,
  prefillCaddieDatagridRows,
} from "../utils/caddieFormioUtils";

/**
 * "Add as new caddie" for the Caddie Name dropdown (IGFEMS-577). Admin participant page only:
 * pass `config = { competitionId, groupId }`; without it the feature is off.
 * Returns the modal element to render next to the form.
 */
export default function useAddCaddie({
  config,
  active,
  containerRef,
  reactFormRef,
  formioWebformRef,
  caddiesListRef,
  caddiesByIdRef,
  skipSavedRestoreRef,
  publishCaddiesById,
  setFormSchema,
  setCapturedSubmission,
  liveDataRef,
  lastEmittedDataRef,
  onDataChange,
  cleanFormId,
  API_URL,
}) {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    const layout = getCaddieDatagridLayout();
    if (!config || !active || !layout?.selectKey) return undefined;
    return attachAddCaddieOption(containerRef.current, {
      selectKey: layout.selectKey,
      onRequestAdd: setTarget,
    });
  }, [config, active, containerRef]);

  const handleCreated = useCallback(
    (person) => {
      const rowIndex = target?.rowIndex ?? 0;
      const option = toCaddieOption(person);
      const merged = [...caddiesListRef.current, option];
      caddiesListRef.current = merged;
      caddiesByIdRef.current = buildCaddiesById(merged);
      publishCaddiesById(caddiesByIdRef.current);
      clearCaddieOptionsCache();

      const fi = reactFormRef.current?.formio || formioWebformRef?.current || null;
      const layout = getCaddieDatagridLayout();
      let data = fi ? snapshotFormioInstanceData(fi) : {};
      if (layout?.gridKey) {
        const grid = Array.isArray(data[layout.gridKey]) ? [...data[layout.gridKey]] : [];
        while (grid.length <= rowIndex) grid.push({});
        grid[rowIndex] = {
          ...grid[rowIndex],
          [layout.selectKey]: String(option._id),
          ...(layout.emailKey ? { [layout.emailKey]: option.email } : {}),
        };
        data = { ...data, [layout.gridKey]: grid };
      }
      data = prefillCaddieDatagridRows(data, caddiesByIdRef.current);

      if (skipSavedRestoreRef) skipSavedRestoreRef.current = true;
      setFormSchema((prev) => {
        if (!prev?.components) return prev;
        const next = JSON.parse(JSON.stringify(prev));
        injectCaddieSelectValues(next.components, caddiesListRef.current, data);
        return next;
      });
      setCapturedSubmission({ formId: cleanFormId, data });
      if (liveDataRef) liveDataRef.current = JSON.parse(JSON.stringify(data));
      lastEmittedDataRef.current = JSON.stringify(data);
      onDataChange?.({ data });
      setTarget(null);
    },
    [
      target,
      caddiesListRef,
      caddiesByIdRef,
      skipSavedRestoreRef,
      publishCaddiesById,
      reactFormRef,
      formioWebformRef,
      setFormSchema,
      setCapturedSubmission,
      liveDataRef,
      lastEmittedDataRef,
      onDataChange,
      cleanFormId,
    ]
  );

  if (!config || !target) return null;
  return (
    <AddCaddieModal
      initialText={target.text}
      config={config}
      API_URL={API_URL}
      onClose={() => setTarget(null)}
      onCreated={handleCreated}
    />
  );
}
